import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/compat/router";
import { usePathname } from "@lib/i18n/navigation-app-router";
import { useCurrentUser } from "@hooks/use-current-user";
import IconSvg from "@components/core/IconSvg";
import MenuListButton from "@components/core/MenuListButton";
import Link from "@components/core/LinkApp";
import AuthOffcanvas from "@components/core/AuthOffcanvas";
import { userState } from "@utils/constants/user";
import subscriptionStatus from "@utils/subscriptionStatus";
import { subscriptionStatuses } from "@utils/constants/subscriptionStatuses";
import Offcanvas from "@components/core/Offcanvas";

function NavbarProfileIcon() {
  const t = useTranslations("common");
  const router = useRouter();
  const user = useCurrentUser();
  const userSubscriptionStatus = subscriptionStatus(user?.subscription);

  const pathname = usePathname();

  const [authOffcanvasOpen, setAuthOffcanvasOpen] = useState(false);
  const [menuListOffcanvasOpen, setMenuListOffcanvasOpen] = useState(false);

  function closeAuthOffcanvas() {
    setAuthOffcanvasOpen(false);
  }

  function closeMenuListOffcanvas() {
    setMenuListOffcanvasOpen(false);
  }

  // Pages router: listen to route change events
  useEffect(() => {
    if (!router) return;

    function handleRouteChangeComplete() {
      setMenuListOffcanvasOpen(false);
    }

    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router]);

  // App router: pathname changes when navigation completes
  useEffect(() => {
    if (router) return;

    setMenuListOffcanvasOpen(false);
  }, [pathname, router]);

  const menuList = () => (
    <div className="d-flex flex-column justify-content-between flex-grow-1 bg-lighter-gray">
      <div>
        <Link href="/profile">
          <MenuListButton className="w-100">{t("navbar.profile.my_profile")}</MenuListButton>
        </Link>
        <Link href="/closets">
          <MenuListButton className="w-100">{t("navbar.profile.my_closets")}</MenuListButton>
        </Link>
        {(user?.state === userState.subscriber ||
          userSubscriptionStatus === subscriptionStatuses.paused) && (
          <Link href="/profile/manage-subscription">
            <MenuListButton className="w-100">{t("navbar.profile.my_subscription")}</MenuListButton>
          </Link>
        )}
        <Link href="/profile/referral">
          <MenuListButton className="w-100">
            {t("navbar.profile.refer_friend_with_icon")}
          </MenuListButton>
        </Link>
        <Link href="/buy_gift_cards">
          <MenuListButton className="w-100">{t("navbar.profile.gift_cards")}</MenuListButton>
        </Link>
      </div>
      <Link
        href="/sign-out"
        className="link-primary w-100 text-center py-3 bg-white rounded-bottom"
        noUnderline
      >
        {t("navbar.profile.logout")}
      </Link>
    </div>
  );

  const userIconVisitor = () => (
    <div className="navbar-profile-section">
      <div className="user-icon">
        <button
          onClick={() => setAuthOffcanvasOpen(true)}
          className="btn-transparent"
        >
          <IconSvg
            name="user-profile"
            className="user-icon-picture"
          />
        </button>
      </div>

      <AuthOffcanvas
        open={authOffcanvasOpen}
        initType="signin"
        onHide={closeAuthOffcanvas}
        onAfterAuth={closeAuthOffcanvas}
      />
    </div>
  );

  const userIconMobile = () => (
    <button
      className="btn-transparent d-lg-none"
      onClick={() => setMenuListOffcanvasOpen(true)}
    >
      <IconSvg
        name="user-profile"
        className="user-icon-picture"
      />
    </button>
  );

  const userIconDesktop = () => (
    <Link
      href="/profile"
      className="d-none d-lg-block"
    >
      <IconSvg
        name="user-profile"
        className="user-icon-picture"
      />
    </Link>
  );

  if (!user) {
    return userIconVisitor();
  }

  return (
    <div className="navbar-profile-section">
      <div className="user-icon">
        {userIconMobile()}
        {userIconDesktop()}
      </div>
      <div className="profile-menu rounded-bottom">{menuList()}</div>

      <Offcanvas
        show={menuListOffcanvasOpen}
        onHide={closeMenuListOffcanvas}
      >
        {menuList()}
      </Offcanvas>
    </div>
  );
}

export default NavbarProfileIcon;
